import {
  forwardRef,
  memo,
  type NamedExoticComponent,
  type RefAttributes,
} from 'react';
import { filter } from 'uinix-fp';
import { LayoutProps as UinixLayoutProps } from 'uinix-ui/lib/components/layout';

import { ResponsiveStyle } from '../../types/ResponsiveStyle';
import { SharedBoxProps } from '../../types/SharedBoxProps';
import { resolvePresetProps } from '../../utils/resolvePresetProps';
import { Box } from '../Box/Box';
import { transformResponsiveStyleValue } from '../Box/utils/transformProps';
import { presets } from './presets';
import { getResponsiveSpacing } from './utils/getResponsiveSpacing';

/**
 * Merges nested arrays of styles (objects, functions, null, undefined) into a
 * flat array.
 */
const mergeStyles = (styles: any[]) => styles.flat();

type Direction = 'row' | 'column' | 'row-reverse' | 'column-reverse';
export interface LayoutProps
  extends
    SharedBoxProps,
    Omit<
      UinixLayoutProps,
      | 'direction'
      | 'spacing'
      | 'wrapSpacing'
      | 'align'
      | 'alignSelf'
      | 'flex'
      | 'justify'
      | 'justifySelf'
    > {
  direction?: ResponsiveStyle<Direction>;
  spacing?: ResponsiveStyle<number | string>;
  wrapSpacing?: ResponsiveStyle<number | string>;
  align?: ResponsiveStyle<string>;
  alignSelf?: ResponsiveStyle<string>;
  flex?: ResponsiveStyle<string | number>;
  justify?: ResponsiveStyle<string>;
  justifySelf?: ResponsiveStyle<string>;
  preset?: keyof typeof presets;
}

export const Layout = memo(
  forwardRef<HTMLElement, LayoutProps>(function Layout(props, ref) {
    const resolvedProps = resolvePresetProps(props, presets);
    const {
      display = 'flex',
      direction,
      inline,
      spacing,
      styleProps,
      styles,
      wrap,
      wrapSpacing,
      ...restProps
    } = resolvedProps;

    const { marginBottom, marginRight } = getResponsiveSpacing(
      transformResponsiveStyleValue(spacing),
      transformResponsiveStyleValue(direction),
    );

    const transformedWrapSpacing = transformResponsiveStyleValue(wrapSpacing);
    const responsiveWrapSpacing = Array.isArray(transformedWrapSpacing)
      ? transformedWrapSpacing.map((space) =>
          space !== undefined && space !== null ? `-${space}` : undefined,
        )
      : `-${transformedWrapSpacing}`;
    const marginTop = wrap && wrapSpacing ? responsiveWrapSpacing : undefined;

    // use styles prop to use children selectors
    // styles prop does not support responsive objects, so getResponsiveSpacing returns as the uinix-ui responsive array structure
    const style = filter((x) => x !== undefined)({
      marginTop,
      '> :not(:last-child)': {
        marginBottom,
        marginRight,
        marginTop: transformedWrapSpacing,
      },
      '> :last-child': {
        marginTop: transformedWrapSpacing,
      },
    });

    return (
      <Box
        {...restProps}
        ref={ref}
        flexDirection={direction}
        display={inline ? 'inline-flex' : display}
        flexWrap={wrap ? 'wrap' : undefined}
        styleProps={styleProps}
        styles={mergeStyles([style, styles])}
      />
    );
  }),
  // Same `SharedBoxProps` index-signature/`PropsWithoutRef` collapse as
  // `Box` — cast back to restore type-checking on callers.
) as NamedExoticComponent<LayoutProps & RefAttributes<HTMLElement>>;

Layout.displayName = 'Layout';
